import React, { useState, useEffect, useContext } from 'react';
import { fmtDate as _fmtDate } from '@/lib/dates';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { AuthContext, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Plus, Eye, FileText, AlertCircle, CheckCircle } from 'lucide-react';

const PropertyEvaluationList = () => {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  const [evaluations, setEvaluations] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedEval, setSelectedEval] = useState(null);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);

  useEffect(() => {
    fetchEvaluations();
    fetchCustomers();
    fetchLocations();
  }, []);

  const fetchEvaluations = async () => {
    try {
      const response = await axios.get(`${API}/property-evaluations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEvaluations(response.data);
    } catch (error) {
      toast.error('Failed to load evaluations');
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/customers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomers(response.data);
    } catch (error) {
      console.error('Failed to fetch customers');
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await axios.get(`${API}/locations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLocations(response.data);
    } catch (error) {
      console.error('Failed to fetch locations');
    }
  };

  const handleView = async (evalId) => {
    try {
      const response = await axios.get(`${API}/property-evaluations/${evalId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedEval(response.data);
      setIsViewDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load evaluation details');
    }
  };

  const getCustomerName = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    return customer ? customer.name : 'Unknown';
  };

  const getLocationName = (locationId) => {
    const location = locations.find(l => l.id === locationId);
    return location ? location.name : 'Unknown';
  };

  const countIssues = (evaluation) => {
    let count = 0;
    const allItems = [...evaluation.exterior_items, ...evaluation.interior_items];
    allItems.forEach(item => {
      if (item.status === 'needs_attention') count++;
    });
    return count;
  };

  const renderInspectionItems = (title, items) => {
    if (!items || items.length === 0) return null;
    
    return (
      <div className="mb-4">
        <h4 className="font-semibold text-sm mb-2 text-slate-700">{title}</h4>
        <div className="space-y-2">
          {items.map((item, index) => (
            <div 
              key={index} 
              className={`p-3 rounded border ${
                item.status === 'needs_attention' ? 'bg-orange-50 border-orange-200' : 'bg-green-50 border-green-200'
              }`}
            >
              <div className="flex items-start justify-between">
                <p className="text-sm font-medium flex-1">{item.item}</p>
                {item.status === 'needs_attention' ? (
                  <AlertCircle className="h-4 w-4 text-orange-600 flex-shrink-0 ml-2" />
                ) : (
                  <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0 ml-2" />
                )}
              </div>
              {item.status === 'needs_attention' && item.description && (
                <p className="text-xs text-slate-600 mt-2 pl-2 border-l-2 border-orange-400">
                  {item.description}
                </p>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Property Evaluations / Evaluaciones de Propiedades
          </h2>
          <p className="text-sm text-slate-600">Quality assessments and inspections</p>
        </div>
        <Button
          onClick={() => navigate('/field/property-evaluation/new')}
          style={{ backgroundColor: '#6B9F3E' }}
          data-testid="create-evaluation-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          New Evaluation
        </Button>
      </div>

      <div className="space-y-3">
        {evaluations.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <FileText className="h-12 w-12 mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500">No evaluations yet.</p>
            </CardContent>
          </Card>
        ) : (
          evaluations.map((evaluation) => {
            const issuesCount = countIssues(evaluation);
            return (
              <Card 
                key={evaluation.id} 
                className="hover:shadow-md transition-shadow"
                style={{ borderLeft: `4px solid ${issuesCount > 0 ? '#F59E0B' : '#6B9F3E'}` }}
                data-testid="evaluation-list-item"
              >
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <CardTitle className="text-base">{evaluation.evaluation_number}</CardTitle>
                        {issuesCount > 0 && (
                          <Badge className="bg-orange-100 text-orange-800">{issuesCount} issue{issuesCount > 1 ? 's' : ''}</Badge>
                        )}
                        {evaluation.forward_to_operations && (
                          <Badge className="bg-red-100 text-red-800">Forwarded to Ops</Badge>
                        )}
                      </div>
                      <p className="text-sm text-slate-600">
                        {getCustomerName(evaluation.customer_id)} - {getLocationName(evaluation.location_id)}
                      </p>
                      <p className="text-xs text-slate-500 mt-1">
                        {evaluation.property_type} • {_fmtDate(evaluation.date_of_evaluation)}
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleView(evaluation.id)}
                      data-testid="view-evaluation-btn"
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                  </div>
                </CardHeader>
              </Card>
            );
          })
        )}
      </div>

      {/* View Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Evaluation Details</DialogTitle>
          </DialogHeader>
          {selectedEval && (
            <div className="space-y-6">
              <div className="bg-olive-50 p-4 rounded-lg border-l-4" style={{ backgroundColor: '#F5F5DC', borderColor: '#BDB76B' }}>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-slate-500">Evaluation Number</Label>
                    <p className="font-bold">{selectedEval.evaluation_number}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Property Type</Label>
                    <p className="font-bold">{selectedEval.property_type}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Customer</Label>
                    <p className="font-medium">{getCustomerName(selectedEval.customer_id)}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Location</Label>
                    <p className="font-medium">{getLocationName(selectedEval.location_id)}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Date</Label>
                    <p className="font-medium">{_fmtDate(selectedEval.date_of_evaluation)}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Evaluator</Label>
                    <p className="font-medium">{selectedEval.evaluator_name}</p>
                  </div>
                </div>
              </div>

              {renderInspectionItems('EXTERIOR', selectedEval.exterior_items)}
              {renderInspectionItems('INTERIOR', selectedEval.interior_items)}

              {selectedEval.general_notes && (
                <div>
                  <Label className="text-xs text-slate-500">General Notes</Label>
                  <p className="mt-1 p-3 bg-slate-50 rounded">{selectedEval.general_notes}</p>
                </div>
              )}

              {selectedEval.forward_to_operations && (
                <div className="p-3 bg-red-50 border border-red-200 rounded">
                  <p className="text-sm font-medium text-red-800">✓ This evaluation was forwarded to Operations</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PropertyEvaluationList;